import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

const DB_NAME = 'ravepal-pmtiles';
const DB_VERSION = 1;
const STORE_NAME = 'pmtiles';

interface PmTilesRecord {
  key: string;
  value: Blob;
}

interface PmTilesDB extends DBSchema {
  [STORE_NAME]: {
    key: string;
    value: PmTilesRecord;
  };
}

let dbPromise: Promise<IDBPDatabase<PmTilesDB>> | null = null;

async function getDB(): Promise<IDBPDatabase<PmTilesDB>> {
  if (!dbPromise) {
    dbPromise = openDB<PmTilesDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

/**
 * Check if a region's .pmtiles file exists in IndexedDB.
 */
export async function hasRegion(regionId: string): Promise<boolean> {
  const db = await getDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const value = await store.get(regionId);
  return value !== undefined;
}

/**
 * Download and stream a .pmtiles file into IndexedDB with progress callback.
 */
export async function downloadRegion(
  regionId: string,
  url: string,
  onProgress?: (pct: number) => void
): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);
  const contentLength = response.headers.get('content-length');
  const total = contentLength ? parseInt(contentLength, 10) : 0;

  const reader = response.body?.getReader();
  if (!reader) throw new Error('Response body is not readable');

  const chunks: BlobPart[] = [];
  let received = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      received += value.length;
      if (total > 0 && onProgress) {
        onProgress(Math.min(100, (received / total) * 100));
      }
    }
  }

  if (onProgress) onProgress(100);

  const blob = new Blob(chunks);
  const db = await getDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  await store.put({ key: regionId, value: blob });
  await tx.done;
}

/**
 * Get a blob URL for a region's .pmtiles file for use with pmtiles:// source.
 * Caller must revoke the URL when done.
 */
export async function getBlobUrl(regionId: string): Promise<string> {
  const db = await getDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const entry = await store.get(regionId);
  await tx.done;
  if (!entry) throw new Error(`Region "${regionId}" not found in IndexedDB`);
  return URL.createObjectURL(entry.value);
}

/**
 * Revoke a blob URL created by getBlobUrl.
 */
export function revokeBlobUrl(url: string): void {
  URL.revokeObjectURL(url);
}
